/* eslint-disable react-hooks/exhaustive-deps */
import { ReactNode, useEffect, useState } from 'react';

import { CSSProperties, DefaultTheme, withTheme } from 'styled-components';

import {
  StyHeader,
  StyModalContainer,
  StyOverlay,
  StyCloseContent,
  StyModalContent,
  StyCloseButton,
  StySeparator,
  StyFooterContent,
  StyHeaderContent,
  StyHeaderWrapper,
  StyIconWrapper,
} from './styles';
import { Typography } from '../Typography';
import { Icon } from '../Icons';

export interface IModal {
  id: string;
  isOpen: boolean;
  onClose(): void;
  width: string;
  height: string;
  hasCloseButton?: boolean;
  customHeader?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  icon?: ReactNode;
  customModal?: ReactNode;
  title?: string;
  subTitle?: string;
  justifyContent?: string;
  closeOnOutsideClick?: boolean;
  theme?: DefaultTheme;
  topRightContent?: ReactNode;
  contentStyle?: CSSProperties;
  headerStyle?: CSSProperties;
  fullheight?: boolean;
}

export const Modal: React.FC<IModal> = withTheme(
  ({
    id,
    isOpen,
    onClose,
    customHeader,
    children,
    contentStyle,
    headerStyle,
    footer,
    width,
    height,
    fullheight = false,
    justifyContent = 'center',
    hasCloseButton = true,
    closeOnOutsideClick = false,
    topRightContent,
    icon,
    customModal,
    title,
    subTitle,
    theme,
  }) => {
    const [showModal, setShowModal] = useState<boolean>(isOpen);
    const [isClosing, setIsClosing] = useState(false);
    const idByTimestamp: string = Date.now().toString();

    const handleClose = (): void => {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        setShowModal(false);
        onClose();
      }, 300);
    };

    const handleCustomClose = (): void => {
      setIsClosing(true);
      setTimeout(() => {
        setShowModal(false);
        setIsClosing(false);
      }, 200);
    };

    useEffect(() => {
      if (isOpen) {
        setShowModal(isOpen);
      }
      if (!isOpen && showModal) {
        handleCustomClose();
      }
    }, [isOpen]);

    useEffect(() => {
      if (showModal) document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = 'unset';
      };
    }, [showModal]);

    useEffect(() => {
      if (showModal && closeOnOutsideClick) {
        const handleOutsideClick = (event: MouseEvent): void => {
          const modal = document.getElementById(
            `${id}-${idByTimestamp}-modal-container`
          );
          if (
            modal &&
            showModal &&
            event.target instanceof Element &&
            !modal.contains(event.target)
          ) {
            handleClose();
          }
        };

        const timeoutDelay = setTimeout(() => {
          document.addEventListener('click', handleOutsideClick);
        }, 0);

        return () => {
          clearTimeout(timeoutDelay);
          document.removeEventListener('click', handleOutsideClick);
        };
      }
    }, [showModal, closeOnOutsideClick, id, idByTimestamp, handleClose]);

    return (
      showModal && (
        <StyOverlay id={id} isClosing={isClosing}>
          <StyModalContainer
            id={`${id}-${idByTimestamp}-modal-container`}
            isClosing={isClosing}
            width={width}
            height={height}
            fullheight={fullheight}
          >
            {customModal && <>{customModal}</>}
            {!customModal && (
              <>
                {(hasCloseButton ||
                  customHeader ||
                  icon ||
                  title ||
                  subTitle) && (
                  <>
                    <StyHeader style={headerStyle}>
                      {customHeader}
                      {!customHeader && (icon || title || subTitle) && (
                        <StyHeaderContent>
                          {!!icon && <StyIconWrapper>{icon}</StyIconWrapper>}
                          <StyHeaderWrapper>
                            <Typography.Label size="medium">
                              {title}
                            </Typography.Label>
                            <Typography.Label
                              size="small"
                              color={theme.neutral[600]}
                            >
                              {subTitle}
                            </Typography.Label>
                          </StyHeaderWrapper>
                        </StyHeaderContent>
                      )}
                      {hasCloseButton && (
                        <StyCloseContent>
                          <StyCloseButton>
                            <Icon
                              type="close-line"
                              size={24}
                              onClick={handleClose}
                            />
                          </StyCloseButton>
                        </StyCloseContent>
                      )}

                      {topRightContent && <>{topRightContent}</>}
                    </StyHeader>
                    <StySeparator />
                  </>
                )}
                <StyModalContent
                  justifyContent={justifyContent}
                  style={contentStyle}
                >
                  {children}
                </StyModalContent>
                {!!footer && (
                  <>
                    <StySeparator />
                    <StyFooterContent>{footer}</StyFooterContent>
                  </>
                )}
              </>
            )}
          </StyModalContainer>
        </StyOverlay>
      )
    );
  }
);
